/**
 * School Partner Account Settings — profile (name, email read-only, phone, country, city) and change password.
 */
import React, { useState, useEffect } from 'react';
import { getApiBaseUrl, getAuthToken } from '../../services/api';

const API_BASE_URL = getApiBaseUrl();

const SchoolAccountSettings = () => {
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const getToken = () =>
    localStorage.getItem('adminToken') || localStorage.getItem('token');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setProfileError(null);
        const res = await fetch(`${API_BASE_URL}/api/school/profile`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.profile) {
          setProfile(data.profile);
          setProfileForm({
            firstName: data.profile.firstName || '',
            lastName: data.profile.lastName || '',
            email: data.profile.email || '',
            phone: data.profile.phone || '',
            country: data.profile.country || '',
            city: data.profile.city || ''
          });
        } else {
          setProfileError(data.message || 'Failed to load profile');
        }
      } catch (e) {
        if (!cancelled) setProfileError(e.message || 'Failed to load profile');
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
    setProfileMessage(null);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/school/profile`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: profileForm.firstName.trim(),
          last_name: profileForm.lastName.trim(),
          phone: profileForm.phone.trim() || null,
          country: profileForm.country.trim() || null,
          city: profileForm.city.trim() || null
        })
      });
      const data = await res.json();
      if (data.success) {
        setProfile(data.profile);
        setProfileForm({
          ...profileForm,
          firstName: data.profile.firstName,
          lastName: data.profile.lastName,
          phone: data.profile.phone || '',
          country: data.profile.country || '',
          city: data.profile.city || ''
        });
        setProfileMessage('Profile updated successfully.');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.name = [data.profile.firstName, data.profile.lastName].filter(Boolean).join(' ') || user.email;
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        setProfileMessage(data.message || 'Failed to update profile');
      }
    } catch (err) {
      setProfileMessage(err.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setPasswordMessage(null);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword.length < 8) {
      setPasswordMessage('New password must be at least 8 characters.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage('New password and confirmation do not match.');
      return;
    }
    setPasswordSaving(true);
    setPasswordMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/user-settings/password`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });
      const data = await res.json();
      if (data.success) {
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordMessage('Password changed successfully.');
      } else {
        setPasswordMessage(data.message || 'Failed to change password');
      }
    } catch (err) {
      setPasswordMessage(err.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="school-tab-content">
        <h1>Account Settings</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="school-tab-content school-account-settings">
      <h1>Account Settings</h1>
      <p className="settings-intro">Manage your school account profile and password.</p>

      {profileError && (
        <div className="settings-error" role="alert">
          {profileError}
        </div>
      )}

      <section className="settings-section">
        <h2>Profile</h2>
        <form onSubmit={handleProfileSubmit} className="settings-form">
          <div className="form-row">
            <label htmlFor="firstName">First name</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={profileForm.firstName}
              onChange={handleProfileChange}
              autoComplete="given-name"
            />
          </div>
          <div className="form-row">
            <label htmlFor="lastName">Last name</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={profileForm.lastName}
              onChange={handleProfileChange}
              autoComplete="family-name"
            />
          </div>
          <div className="form-row">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={profileForm.email}
              readOnly
              disabled
              title="Email cannot be changed here"
            />
            <span className="field-hint">Email cannot be changed here.</span>
          </div>
          <div className="form-row">
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              name="phone"
              type="text"
              value={profileForm.phone}
              onChange={handleProfileChange}
              autoComplete="tel"
            />
          </div>
          <div className="form-row">
            <label htmlFor="country">Country</label>
            <input
              id="country"
              name="country"
              type="text"
              value={profileForm.country}
              onChange={handleProfileChange}
              autoComplete="country-name"
            />
          </div>
          <div className="form-row">
            <label htmlFor="city">City</label>
            <input
              id="city"
              name="city"
              type="text"
              value={profileForm.city}
              onChange={handleProfileChange}
              autoComplete="address-level2"
            />
          </div>
          {profileMessage && (
            <div className={`form-message ${profileMessage.includes('success') ? 'success' : 'error'}`}>
              {profileMessage}
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="btn-save" disabled={profileSaving}>
              {profileSaving ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </form>
      </section>

      <section className="settings-section">
        <h2>Change password</h2>
        <form onSubmit={handlePasswordSubmit} className="settings-form">
          <div className="form-row">
            <label htmlFor="currentPassword">Current password</label>
            <input
              id="currentPassword"
              name="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              autoComplete="current-password"
            />
          </div>
          <div className="form-row">
            <label htmlFor="newPassword">New password</label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              autoComplete="new-password"
              minLength={8}
            />
            <span className="field-hint">At least 8 characters.</span>
          </div>
          <div className="form-row">
            <label htmlFor="confirmPassword">Confirm new password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              autoComplete="new-password"
            />
          </div>
          {passwordMessage && (
            <div className={`form-message ${passwordMessage.includes('success') ? 'success' : 'error'}`}>
              {passwordMessage}
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="btn-save" disabled={passwordSaving}>
              {passwordSaving ? 'Updating...' : 'Change password'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default SchoolAccountSettings;
